import { useEffect, useState } from 'react'

import { FxAlert, FxButton, FxIcon } from '@/components/fx'
import { DialogShell } from './DialogShell'
import {
  RECORD_COLUMN_TYPES,
  RECORD_COLUMN_TYPE_LABELS,
  SCHEMA_PROBLEM_MESSAGES,
  schemaProblem,
  serializeRecordSchema,
  type RecordColumn,
  type RecordSchema,
} from './recordSchema'
import { useTemplateSetting } from './useTemplateSetting'

/**
 * The column editor.
 *
 * A list of rows rather than a JSON box: the schema is the export's header
 * row, and the people who care about it are the people who open the Excel
 * file, not the people who read JSONB. Each row is a real `<input>` with its
 * own label, so the whole thing is keyboard-operable and a screen reader can
 * say which column it is on.
 *
 * `instructions` (the model's prompt for this schema) is not editable and is
 * written back untouched — rewriting a prompt through a settings form is a
 * different job with different stakes.
 *
 * **There is no "auf Standard zurücksetzen".** Resetting would mean sending
 * `recordSchema: null`, and the Postgres PATCH handler forwards the key only
 * when it is a non-nil map, so that request answers 200 and changes nothing.
 * The editor therefore refuses an empty column list instead of pretending.
 */
export function RecordSchemaDialog({
  open,
  onClose,
  schema,
  isDefault,
}: {
  open: boolean
  onClose: () => void
  schema: RecordSchema
  isDefault: boolean
}) {
  const save = useTemplateSetting()
  const [draft, setDraft] = useState<RecordSchema>(schema)

  // Re-seed each time it opens, so cancelling really discards. Keyed on the
  // stored schema too: a save elsewhere must not leave a stale draft behind.
  useEffect(() => {
    if (open) {
      setDraft({ ...schema, columns: schema.columns.map((column) => ({ ...column })) })
      save.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schema])

  const problem = schemaProblem(draft)

  const update = (index: number, patch: Partial<RecordColumn>) =>
    setDraft((current) => ({
      ...current,
      columns: current.columns.map((column, i) => (i === index ? { ...column, ...patch } : column)),
    }))

  const remove = (index: number) =>
    setDraft((current) => ({
      ...current,
      columns: current.columns.filter((_, i) => i !== index),
    }))

  const add = () =>
    setDraft((current) => ({ ...current, columns: [...current.columns, { name: '' }] }))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (problem) return
    save.mutate({ recordSchema: serializeRecordSchema(draft) }, { onSuccess: onClose })
  }

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title="Datensatz-Schema bearbeiten"
      description="Jede Spalte hier wird eine Spalte im Tab Datensätze und in der Excel-Datei. Die Art einer Spalte steuert die Prüfungen nach der Erkennung — etwa ob eine PLZ zum Land passt."
      wide
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {isDefault && (
          <FxAlert tone="info" title="Aus dem Standard wird eine eigene Kopie">
            Dieses Musterformular benutzt bisher das eingebaute Kontakt-Schema. Mit dem Speichern
            bekommt es ein eigenes; spätere Änderungen am eingebauten Schema wirken sich dann nicht
            mehr darauf aus.
          </FxAlert>
        )}

        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {draft.columns.map((column, index) => (
            <li
              key={index}
              className="flex flex-col gap-2 rounded-sm bg-ground p-3 xs:flex-row xs:items-end"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <label
                  className="text-small text-text-muted"
                  htmlFor={`schema-name-${index}`}
                >
                  Name der {index + 1}. Spalte
                </label>
                <input
                  id={`schema-name-${index}`}
                  value={column.name}
                  onChange={(event) => update(index, { name: event.target.value })}
                  className="h-control w-full rounded-md bg-surface px-3 text-body text-text"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-small text-text-muted" htmlFor={`schema-type-${index}`}>
                  Art
                </label>
                <select
                  id={`schema-type-${index}`}
                  value={column.type ?? ''}
                  onChange={(event) => update(index, { type: event.target.value })}
                  className="h-control w-full rounded-md bg-surface px-3 text-body text-text xs:w-[200px]"
                >
                  <option value="">Text</option>
                  {RECORD_COLUMN_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {RECORD_COLUMN_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Spalte „${column.name || index + 1}“ entfernen`}
                className="h-control shrink-0 self-end rounded-md px-2 text-text-muted hover:text-danger-text"
              >
                <FxIcon name="delete" size={22} />
              </button>
            </li>
          ))}
        </ul>

        <div>
          <FxButton type="button" variant="tonal" icon="add" onClick={add}>
            Spalte hinzufügen
          </FxButton>
        </div>

        {problem && (
          <p role="alert" className="m-0 text-small text-danger-text">
            {SCHEMA_PROBLEM_MESSAGES[problem]}
          </p>
        )}
        {save.isError && (
          <p role="alert" className="m-0 text-small text-danger-text">
            Das Schema konnte nicht gespeichert werden. Ihre Eingaben stehen noch hier.
          </p>
        )}

        <div className="flex justify-end gap-3">
          <FxButton type="button" variant="outlined" onClick={onClose}>
            Abbrechen
          </FxButton>
          <FxButton type="submit" disabled={problem != null || save.isPending}>
            {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </FxButton>
        </div>
      </form>
    </DialogShell>
  )
}
